// src/components/Dashboard/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usageService } from '../../services/usageService';
import { tenantService } from '../../services/tenantService';
import { UsageSummary, Tenant } from '../../types';
import { Users, Key, Activity, BarChart, Building2 } from 'lucide-react';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const { user, isSystemAdmin } = useAuth();
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isSystemAdmin()) {
      fetchTenants();
    } else {
      fetchSummary();
    }
  }, []);

  const fetchSummary = async () => {
    try {
      setError('');
      const response = await usageService.getSummary();
      setSummary(response.data);
    } catch (error: any) {
      console.error('Error fetching summary:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      setError('');
      const response = await tenantService.getAll();
      setTenants(response.data);
    } catch (error: any) {
      console.error('Error fetching tenants:', error);
      setError('Failed to load tenant data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (error) return <div className="error-message">{error}</div>;

  // SystemAdmin Dashboard
  if (isSystemAdmin()) {
    const activeTenants = tenants.filter(t => t.isActive).length;
    const totalUsers = tenants.reduce((sum, t) => sum + t.userCount, 0);
    const totalServices = tenants.reduce((sum, t) => sum + t.serviceCount, 0);
    const totalConsumers = tenants.reduce((sum, t) => sum + t.consumerCount, 0);

    return (
      <div className="dashboard">
        <h2 className="page-title">System Administration</h2>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <Building2 size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Active Tenants</p>
              <p className="stat-value">{activeTenants}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Users</p>
              <p className="stat-value">{totalUsers}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Activity size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Services</p>
              <p className="stat-value">{totalServices}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Key size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Consumers</p>
              <p className="stat-value">{totalConsumers}</p>
            </div>
          </div>
        </div>

        {tenants.length > 0 && (
          <div className="card">
            <h3>Tenants</h3>
            <div className="top-consumers">
              {tenants.map((tenant) => (
                <div key={tenant.id} className="consumer-item">
                  <span>
                    {tenant.companyName}
                    {!tenant.isActive && <span className="text-danger"> (Inactive)</span>}
                  </span>
                  <span className="requests">
                    {tenant.serviceCount} services • {tenant.consumerCount} consumers • {tenant.userCount} users
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Tenant User Dashboard
  return (
    <div className="dashboard">
      <h2 className="page-title">Dashboard</h2>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Consumers</p>
            <p className="stat-value">{summary?.totalConsumers || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Key size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Active API Keys</p>
            <p className="stat-value">{summary?.totalApiKeys || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Activity size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Today's Requests</p>
            <p className="stat-value">{summary?.todayRequests || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <BarChart size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Active Keys (7d)</p>
            <p className="stat-value">{summary?.activeKeys || 0}</p>
          </div>
        </div>
      </div>

      {summary?.topConsumers && summary.topConsumers.length > 0 && (
        <div className="card">
          <h3>Top API Consumers Today</h3>
          <div className="top-consumers">
            {summary.topConsumers.map((consumer, index) => (
              <div key={index} className="consumer-item">
                <span>{consumer.consumer}</span>
                <span className="requests">{consumer.requests} requests</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
